'use client';

import NotificationRepository from 'data/repositories/NotificationRepository';
import { getToken, onMessage } from 'firebase/messaging';
import { useTranslations } from 'next-intl';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MESSAGE_TYPES } from 'types/constants';
import { messaging } from 'utils/firebase';

interface Notification {
  title: string;
  body: string;
  icon?: string;
}

interface NotificationsContextProps {
  notifications?: Notification[];
  addNotification?: (notification: Notification) => void;
}

const NotificationsContext = createContext<NotificationsContextProps | undefined>(undefined);

export const NotificationsWrapper = ({
  children,
  isAuthenticated,
}: {
  children: React.ReactNode;
  isAuthenticated: boolean;
}) => {
  const t = useTranslations();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const isTokenSent = useRef(false);

  const addNotification = useCallback((notification: Notification) => {
    setNotifications((prev) => [...prev, notification]);
  }, []);

  const value = useMemo(
    () => ({ notifications, addNotification }),
    [notifications, addNotification],
  );

  useEffect(() => {
    if (!messaging) return;

    const listenForMessages = () => {
      onMessage(messaging, (payload) => {
        if (payload.notification) {
          addNotification({
            title: payload.notification.title || t('notifications_new'),
            body: payload.notification.body || '',
            icon: payload.notification.image || '/arbiLogo76.png',
          });
        }
      });
    };

    listenForMessages();
  }, [addNotification]);

  useEffect(() => {
    if (!isAuthenticated || isTokenSent.current || !messaging) return;

    const requestAndSendToken = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.warn('Permission denied for notifications');
          return;
        }

        const vapidKey = process.env.FIREBASE_VAPID_KEY;
        if (!vapidKey) {
          console.error('VAPID_KEY not found in .env');
          return;
        }

        const token = await getToken(messaging, { vapidKey });
        if (!token) {
          console.warn('Received empty FCM token');
          return;
        }

        await NotificationRepository.registerPushToken(token);

        isTokenSent.current = true;
      } catch (error) {
        console.error('Error getting FCM token', error);
      }
    };

    requestAndSendToken();
  }, [isAuthenticated]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
          type: MESSAGE_TYPES.INIT_TRANSLATIONS,
          translations: {
            notifications_new: t('notifications_new'),
            push_message: t('notifications_newMessage'),
            no_data: t('noData'),
          },
        });
      });
    }
  }, [t]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsWrapper');
  }
  return context;
};
